/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod';
import {
  ACRPresetPayloadSchema,
  ACLClarkePresetPayloadSchema,
  ACLCompetitorPresetPayloadSchema,
  ConsumptionInfoSchema,
  ContractedDemandInfoSchema,
  EnergyPriceInfoDTOSchema,
  MarginFeeSchema,
  RawEnergyPriceProjectionComponentSchema,
  PriceListCompositionItemSchema,
} from '@schemas/scenarios-simulator/schema';
import { IPresetPayload, IPresetTypeEnum } from '@schemas/scenarios-simulator/types';
import { getRequiredHistoryFields } from '@schemas/scenarios-simulator/validations/helpers/tariff-helpers';
import { getTariffModality, getTariffVoltageType } from '@utils/tariff-helpers';

type SupportedPresetType = 'ACR' | 'CLARKE_ACL' | 'COMPETITOR_ACL';

const PRESET_SCHEMAS: Record<SupportedPresetType, z.AnyZodObject> = {
  ACR: ACRPresetPayloadSchema,
  CLARKE_ACL: ACLClarkePresetPayloadSchema,
  COMPETITOR_ACL: ACLCompetitorPresetPayloadSchema,
};

const CONSUMPTION_INFO_KEYS = Object.keys((ConsumptionInfoSchema as z.AnyZodObject).shape);
const CONTRACTED_DEMAND_INFO_KEYS = Object.keys((ContractedDemandInfoSchema as z.AnyZodObject).shape);
const ENERGY_PRICE_INFO_KEYS = Object.keys((EnergyPriceInfoDTOSchema as z.AnyZodObject).shape);
const MARGIN_FEE_KEYS = Object.keys((MarginFeeSchema as z.AnyZodObject).shape);
const RAW_ENERGY_PRICE_KEYS = Object.keys((RawEnergyPriceProjectionComponentSchema as z.AnyZodObject).shape);
const PRICE_LIST_COMPOSITION_ITEM_KEYS = Object.keys((PriceListCompositionItemSchema as z.AnyZodObject).shape);

export const PresetFieldMap: Record<SupportedPresetType, string[]> = {
  ACR: Object.keys(PRESET_SCHEMAS.ACR.shape),
  CLARKE_ACL: Object.keys(PRESET_SCHEMAS.CLARKE_ACL.shape),
  COMPETITOR_ACL: Object.keys(PRESET_SCHEMAS.COMPETITOR_ACL.shape),
};

function isObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function pickAllowedObjectKeys<T extends Record<string, any>>(value: T, allowedKeys: string[]): Partial<T> {
  const allowed = new Set(allowedKeys);
  return Object.entries(value).reduce((acc, [key, item]) => {
    if (!allowed.has(key)) return acc;
    acc[key as keyof T] = item;
    return acc;
  }, {} as Partial<T>);
}

function sanitizeConsumptionInfoByConditions(
  payload: Partial<IPresetPayload>,
  initialConsumptionInfo: unknown,
): Partial<IPresetPayload['consumptionInfo']> | undefined {
  if (!isObject(initialConsumptionInfo)) {
    return undefined;
  }

  const consumptionInfo = pickAllowedObjectKeys(initialConsumptionInfo, CONSUMPTION_INFO_KEYS) as Partial<
    IPresetPayload['consumptionInfo']
  >;

  const voltageType = getTariffVoltageType(String(payload.tariffGroup));
  const tariffModality = getTariffModality(String(payload.tariffGroup));
  const requiredHistory = getRequiredHistoryFields(payload.tariffGroup);

  if (!requiredHistory.showConsumption) {
    delete consumptionInfo?.consumptionLowVoltage;
  }
  if (!requiredHistory.showOffPeakConsumption) {
    delete consumptionInfo?.consumptionOffPeak;
  }
  if (!requiredHistory.showPeakConsumption) {
    delete consumptionInfo?.consumptionPeak;
  }

  if (voltageType !== 'LOW_VOLTAGE') {
    delete consumptionInfo?.operationHoursRange;
  }

  if (voltageType === 'LOW_VOLTAGE') {
    if (isObject(consumptionInfo?.operationHoursRange)) {
      if (payload.aux__useOpenInWeekends !== true) {
        delete consumptionInfo?.operationHoursRange.saturday;
        delete consumptionInfo?.operationHoursRange.sunday;
      }
    }
    delete consumptionInfo?.contractedDemandInfo;
  }

  if (voltageType === 'MEDIUM_VOLTAGE') {
    delete consumptionInfo?.consumptionLowVoltage;

    if (isObject(consumptionInfo?.contractedDemandInfo)) {
      const cd = pickAllowedObjectKeys(consumptionInfo.contractedDemandInfo, CONTRACTED_DEMAND_INFO_KEYS) as any;

      if (tariffModality === 'GREEN') {
        delete cd.contractedDemandPeak;
      }

      consumptionInfo.contractedDemandInfo = Object.keys(cd).length > 0 ? cd : undefined;
    }
  }

  return Object.keys(consumptionInfo ?? {}).length > 0 ? consumptionInfo : undefined;
}

function sanitizeNestedStructures(payload: Partial<IPresetPayload>): Partial<IPresetPayload> {
  const next = { ...payload };

  if (isObject(next.consumptionInfo)) {
    next.consumptionInfo = sanitizeConsumptionInfoByConditions(next, next.consumptionInfo) as
      | IPresetPayload['consumptionInfo']
      | undefined;
  }

  if (isObject(next.energyPriceInfo)) {
    next.energyPriceInfo = pickAllowedObjectKeys(next.energyPriceInfo, ENERGY_PRICE_INFO_KEYS) as any;

    if (next.aux__useManualPrices !== true) {
      // @ts-expect-error - property only exists in some presets, but if aux__useManualPrices is false, manualAclPricesHasChargesCoverage should not be present
      delete next.energyPriceInfo.manualAclPricesHasChargesCoverage;
    }
  }

  if (isObject(next.marginFee)) {
    next.marginFee = pickAllowedObjectKeys(next.marginFee, MARGIN_FEE_KEYS) as any;
  }

  if (isObject(next.rawEnergyPrice)) {
    next.rawEnergyPrice = pickAllowedObjectKeys(next.rawEnergyPrice, RAW_ENERGY_PRICE_KEYS) as any;
  }

  if (Array.isArray(next.priceListComposition)) {
    next.priceListComposition = next.priceListComposition
      .filter((item) => isObject(item))
      .map((item) => pickAllowedObjectKeys(item, PRICE_LIST_COMPOSITION_ITEM_KEYS)) as any;
  }

  if (getTariffVoltageType(String(next.tariffGroup)) !== 'LOW_VOLTAGE') {
    delete next.companyType;
  }

  return next;
}

export function sanitizePresetPayloadByPreset(
  presetType: IPresetTypeEnum,
  presetPayload: IPresetPayload | null | undefined,
): Partial<IPresetPayload> {
  if (!presetType || presetType === 'undefined') {
    return isObject(presetPayload) ? { ...presetPayload } : {};
  }

  if (!isObject(presetPayload)) {
    return {};
  }

  const allowedKeys = PresetFieldMap[presetType as SupportedPresetType];

  if (!allowedKeys) {
    return { ...presetPayload };
  }

  const topLevelSanitized = pickAllowedObjectKeys(presetPayload, allowedKeys) as Partial<IPresetPayload>;
  return sanitizeNestedStructures(topLevelSanitized);
}
